import { createContext, useContext, FC, ReactNode, ComponentType } from 'react';
import { useJsApiLoader } from '@react-google-maps/api';

interface MapsContextValue {
  isLoaded: boolean;
  loadError: Error | undefined;
}

interface MapsProviderProps {
  children: ReactNode;
}

const MapsContext = createContext<MapsContextValue | null>(null);

// Define libraries array as a static constant outside component to prevent re-renders
const LIBRARIES: ("places")[] = ["places"];

export const MapsProvider: FC<MapsProviderProps> = ({ children }) => {
  // useJsApiLoader loads the Google Maps script in the background
  // without blocking the rest of the app from rendering
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
    libraries: LIBRARIES,
  });

  return (
    <MapsContext.Provider value={{ isLoaded, loadError }}>
      {children}
    </MapsContext.Provider>
  );
};

/**
 * Hook to access Maps context.
 * Must be used within a MapsProvider.
 */
export const useMaps = (): MapsContextValue => {
  const context = useContext(MapsContext);
  if (!context) {
    throw new Error('useMaps must be used within a MapsProvider');
  }
  return context;
};

/**
 * HOC to wrap components that require Google Maps.
 * Use this to lazily load Maps only on pages that need it.
 * 
 * @example
 * const ProjectDetailWithMaps = withMapsProvider(ProjectDetail);
 */
export const withMapsProvider = <P extends object>(Component: ComponentType<P>) => {
  const WrappedComponent: FC<P> = (props) => (
    <MapsProvider>
      <Component {...props} />
    </MapsProvider>
  );
  
  // Set display name for debugging
  WrappedComponent.displayName = `withMapsProvider(${Component.displayName || Component.name || 'Component'})`;
  
  return WrappedComponent;
};
